import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ReviewNoteView } from '@/api/lcReviewNote'
import type { ManualGradeCredit } from '@/types/coaching'
import { useGradeReviewNoteItem } from '@/hooks/coaching/useReviewNote'
import { GradingSheet } from './GradingSheet'
import { reviewNoteRowsOf, type GradingSheetRow } from './gradingRows'
import { gradingDraftScope } from '@/hooks/coaching/useGradingDraft'

interface Props {
  learnerId: number
  reviewNote: ReviewNoteView
  canGrade: boolean
  onRequestCoaching?: (quizId: string) => void
  /** 전 문항 채점 시 노출되는 [리뷰노트 완료] — 상위 패널의 complete mutation */
  onComplete?: () => void
  completing?: boolean
}

/**
 * 리뷰노트 채점표 — 세트 문항을 O/X 행으로 펼치고,
 * 행 저장을 기존 `POST …/review-notes/{setId}/items/{quizId}/grade`(manualGrade=true, answer 없음)에 매핑한다.
 * 백엔드에 partialCredit이 없어 △는 제공하지 않는다.
 */
export function ReviewNoteGradingSheet({
  learnerId,
  reviewNote,
  canGrade,
  onRequestCoaching,
  onComplete,
  completing = false,
}: Props) {
  const { t } = useTranslation('coaching')
  const grade = useGradeReviewNoteItem(learnerId, reviewNote.setId)
  const rows = useMemo(() => reviewNoteRowsOf(reviewNote), [reviewNote])

  const saveRow = async (row: GradingSheetRow, value: ManualGradeCredit) => {
    await grade.mutateAsync({ quizId: row.key, isCorrect: value === 1, manualGrade: true })
  }

  const allGraded = reviewNote.totalCount > 0 && reviewNote.gradedCount === reviewNote.totalCount
  const showComplete = canGrade && onComplete != null && reviewNote.status !== 'COMPLETED' && allGraded

  return (
    <GradingSheet
      draftScope={gradingDraftScope.reviewNote(reviewNote.setId)}
      testId="review-note-grading-sheet"
      title={t('gradingMode.sheetTitleReviewNote')}
      rows={rows}
      readOnly={!canGrade}
      allowMarkAllCorrect
      saveRow={saveRow}
      onRequestCoaching={onRequestCoaching}
      emptyMessage={t('reviewNote.empty')}
      footerActions={showComplete ? (
        <button
          type="button"
          data-testid="review-note-grading-complete"
          disabled={completing}
          onClick={onComplete}
          className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {completing ? t('reviewNote.completing') : t('reviewNote.complete')}
        </button>
      ) : null}
    />
  )
}
